import React, { useState } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Info, TrendingUp, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

// Plain-language, business-user explanations of each forecasting method so a
// non-technical reader understands what the chosen algorithm actually does.
const ALGO_DESCRIPTIONS = [
  [/seasonal\s*naive/i, 'repeats last year\u2019s same-period values \u2014 good when the metric follows a steady yearly pattern.'],
  [/damped/i, 'projects the recent trend forward but eases it off over time, so long-range forecasts don\u2019t over-shoot.'],
  [/holt.?winters|triple/i, 'captures the level, the trend, and the repeating seasonal cycle together \u2014 for metrics with both a trend and a yearly pattern.'],
  [/holt|double/i, 'tracks both the current level and its trend and carries that trend forward \u2014 for metrics with steady growth or decline.'],
  [/seasonal\s*arima|sarima/i, 'a statistical model that learns from recent values, past forecast errors, and the repeating seasonal cycle \u2014 for metrics with both momentum and a yearly pattern.'],
  [/arima/i, 'a statistical model that learns from recent values and its own past errors \u2014 for metrics with momentum but no fixed season.'],
  [/exponential|\bses\b/i, 'a smoothed average that weights recent periods more than older ones \u2014 for fairly level metrics with no clear trend or season.'],
  [/moving average/i, 'averages the last few periods to smooth out noise \u2014 best for flat, choppy metrics.'],
  [/linear|trend/i, 'fits a straight trend line through history and extends it \u2014 for steady, consistent growth or decline.'],
  [/naive|last value/i, 'assumes the next periods look like the most recent value \u2014 a simple baseline with no trend or seasonality.'],
];
function algoDescription(model) {
  if (!model) return '';
  const hit = ALGO_DESCRIPTIONS.find(([re]) => re.test(model));
  return hit ? `This method ${hit[1]}` : '';
}


// Generate future period labels by continuing the cadence of the history labels.
export function futureLabels(historyPeriods, n) {
  const last = historyPeriods[historyPeriods.length - 1];
  const prev = historyPeriods[historyPeriods.length - 2];
  const asNum = (x) => (typeof x === 'number' ? x : (typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x.trim()) ? Number(x) : null));
  const ln = asNum(last), pn = asNum(prev);
  if (ln != null && pn != null) {
    const step = (ln - pn) || 1;
    return Array.from({ length: n }, (_, i) => String(Math.round(ln + step * (i + 1))));
  }
  const mm = typeof last === 'string' && last.match(/^(\d{4})-(\d{2})$/);
  if (mm) {
    let y = +mm[1], mo = +mm[2];
    return Array.from({ length: n }, () => { mo++; if (mo > 12) { mo = 1; y++; } return `${y}-${String(mo).padStart(2, '0')}`; });
  }
  return Array.from({ length: n }, (_, i) => `${last} +${i + 1}`);
}

/**
 * Forecast result chart. Renders history + projection (dashed) + confidence band,
 * an algorithm picker (Auto or a specific one) with a horizon control, and an
 * "algorithm chosen & why" popover. Re-runs against /ai/forecast/series.
 */
// Distinct line colors for multi-series (per-breakdown) forecasts.
const MULTI_COLORS = ['#22d3ee', '#818cf8', '#f472b6', '#34d399', '#fbbf24', '#f87171', '#a78bfa', '#60a5fa', '#fb923c', '#4ade80', '#e879f9', '#2dd4bf', '#f59e0b', '#38bdf8'];

// Compact Y-axis formatter — full numbers (e.g. "80,000,000") overflow the
// narrow axis gutter and get clipped off-canvas, so the axis looked blank.
function compactAxisValue(v) {
  if (v == null || (typeof v === 'number' && !isFinite(v))) return '';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  const a = Math.abs(n);
  const trim = (x) => x.toFixed(1).replace(/\.0$/, '');
  if (a >= 1e9) return trim(n / 1e9) + 'B';
  if (a >= 1e6) return trim(n / 1e6) + 'M';
  if (a >= 1e3) return trim(n / 1e3) + 'k';
  return String(Math.round(n));
}

// Trust rating for a forecast. Prefers the chosen model's backtest error
// (MAPE, lower = better); falls back to the average relative width of the
// confidence bands when no backtest score is available.
function deriveConfidence(mape, forecastPoints) {
  let pct = null, basis = '';
  if (mape != null && isFinite(mape)) { pct = Number(mape); basis = `backtest error ${pct}%`; }
  else if (forecastPoints && forecastPoints.length) {
    const rel = forecastPoints
      .filter(p => p.lower != null && p.upper != null && p.value)
      .map(p => Math.abs((p.upper - p.lower) / 2 / (p.value || 1)));
    if (rel.length) { pct = Math.round((rel.reduce((a, b) => a + b, 0) / rel.length) * 100); basis = `±${pct}% projection band`; }
  }
  if (pct == null) return null;
  const level = pct <= 10 ? 'High' : pct <= 25 ? 'Medium' : 'Low';
  const cls = level === 'High' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
    : level === 'Medium' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
      : 'bg-rose-500/15 text-rose-300 border-rose-500/30';
  return { level, cls, basis };
}

function ConfidenceBadge({ conf }) {
  if (!conf) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-semibold ${conf.cls}`}
      data-testid="forecast-confidence-badge"
      title={`Confidence: ${conf.level} — based on ${conf.basis}. Lower error / narrower bands mean a more reliable forecast.`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />{conf.level} confidence
    </span>
  );
}

// Multi-series forecast — one history + projection line per breakdown value
// (e.g. per line of business). Each series was forecast independently on the
// backend with its own auto-selected model.
function MultiForecastChart({ forecast, formatValue }) {
  const [fc, setFc] = useState(forecast);
  const [focusIdx, setFocusIdx] = useState(null);
  const [busy, setBusy] = useState(false);
  const {
    measure_label, breakdown_label, periods = [], boundary_period, groups = [], dropped_periods = [],
    unit, horizon, model_id, source_question, context_question, top_n, total_series, grains = ['year', 'quarter', 'month'],
  } = fc;
  const rebuild = async (patch) => {
    if (!model_id) { toast.error('This forecast can’t be adjusted here.'); return; }
    setBusy(true);
    try {
      const res = await api.forecastRebuild({ model_id, source_question, context_question, unit, horizon, top_n, ...patch });
      if (res?.result?.forecast) { setFc(res.result.forecast); setFocusIdx(null); }
    } catch (e) { toast.error(e.message || 'Could not adjust the forecast'); }
    finally { setBusy(false); }
  };
  if (!groups.length || !periods.length) return null;
  const fmt = (v) => {
    if (v == null) return '';
    if (formatValue) { const out = formatValue(v); if (out || out === 0) return out; }
    return typeof v === 'number' ? v.toLocaleString() : v;
  };
  const focused = focusIdx != null && groups[focusIdx] ? groups[focusIdx] : null;
  const data = periods.map((p) => {
    const row = { period: p };
    groups.forEach((g, i) => {
      const pt = g.series.find(s => s.period === p);
      if (!pt) return;
      if (pt.type === 'history') row[`h${i}`] = pt.value;
      else row[`f${i}`] = pt.value;
      if (focusIdx === i && pt.type === 'forecast' && pt.lower != null && pt.upper != null) row.band = [pt.lower, pt.upper];
    });
    if (p === boundary_period) groups.forEach((g, i) => { if (row[`h${i}`] != null) row[`f${i}`] = row[`h${i}`]; });
    return row;
  });
  const nameByKey = {};
  groups.forEach((g, i) => { nameByKey[`h${i}`] = g.name; nameByKey[`f${i}`] = `${g.name} (forecast)`; });
  const visible = (i) => focusIdx == null || focusIdx === i;
  const onLegendClick = (o) => {
    const k = String(o?.dataKey || '');
    const i = parseInt(k.replace('h', ''), 10);
    if (!Number.isNaN(i)) setFocusIdx(prev => (prev === i ? null : i));
  };

  return (
    <div className="glass-card rounded-2xl p-4 border border-border/30 relative" data-testid="forecast-chart">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-indigo-400" />
          <span className="text-xs font-heading font-semibold text-foreground">{measure_label} · Forecast by {breakdown_label}</span>
          <ConfidenceBadge conf={deriveConfidence(null, groups.flatMap(g => g.series.filter(s => s.type === 'forecast')))} />
        </div>
        <div className="flex items-center gap-2">
          <select value={unit} onChange={(e) => rebuild({ unit: e.target.value })} disabled={busy} className="bg-muted/30 border border-border/40 rounded-lg text-[10px] text-foreground px-1.5 py-1 outline-none" data-testid="forecast-grain-select" title="Time grain">
            {grains.map(g => <option key={g} value={g}>{g === 'year' ? 'Yearly' : g === 'quarter' ? 'Quarterly' : g === 'month' ? 'Monthly' : g}</option>)}
          </select>
          {(total_series || groups.length) > 3 && (
            <select value={top_n || 'all'} onChange={(e) => rebuild({ top_n: e.target.value === 'all' ? null : Number(e.target.value) })} disabled={busy} className="bg-muted/30 border border-border/40 rounded-lg text-[10px] text-foreground px-1.5 py-1 outline-none" data-testid="forecast-topn-select" title="How many lines to show">
              <option value="all">All lines</option>
              <option value="8">Top 8</option>
              <option value="5">Top 5</option>
              <option value="3">Top 3</option>
            </select>
          )}
          {focused && (
            <button onClick={() => setFocusIdx(null)} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-[10px] font-medium transition-colors" data-testid="forecast-clear-focus">
              Isolated: {focused.name} · Show all
            </button>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 text-[10px] font-medium transition-colors" data-testid="forecast-algo-info" title="Model per series">
                <Sparkles className="w-3 h-3" /> {groups.length} series
                <Info className="w-3 h-3 opacity-60" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 overflow-hidden" align="end" data-testid="forecast-algo-popover">
              <div className="px-4 py-3 border-b border-border/40 bg-indigo-500/5">
                <p className="text-xs font-heading font-semibold text-foreground flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-indigo-400" /> Model chosen per {breakdown_label}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Click a series in the legend to isolate it with its confidence band.</p>
              </div>
              <div className="px-4 py-3 space-y-1.5 max-h-72 overflow-auto">
                {groups.map((g, i) => (
                  <button key={g.name} onClick={() => setFocusIdx(prev => (prev === i ? null : i))} className="w-full flex items-center justify-between gap-2 hover:bg-muted/20 rounded px-1 py-0.5">
                    <span className="flex items-center gap-1.5 text-[11px] text-foreground/85 truncate">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: MULTI_COLORS[i % MULTI_COLORS.length] }} />
                      <span className="truncate">{g.name}</span>
                    </span>
                    <span className="text-[10px] text-indigo-300 font-medium flex-shrink-0">{g.model}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="w-full relative" style={{ height: 360 }}>
        {busy && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-xl bg-background/70 backdrop-blur-sm" data-testid="forecast-busy">
            <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
            <span className="text-xs text-muted-foreground">Crunching {total_series || groups.length} series…</span>
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" opacity={0.3} />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#a1a1aa' }} interval={0} />
            <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} tickFormatter={compactAxisValue} width={52} />
            <Tooltip
              contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 11 }}
              formatter={(v, key) => [Array.isArray(v) ? `${fmt(v[0])} – ${fmt(v[1])}` : fmt(v), key === 'band' ? 'Confidence' : (nameByKey[key] || key)]}
            />
            <Legend wrapperStyle={{ fontSize: 10, cursor: 'pointer' }} onClick={onLegendClick} />
            {focused && <Area dataKey="band" name="Confidence" legendType="none" stroke="none" fill={MULTI_COLORS[focusIdx % MULTI_COLORS.length]} fillOpacity={0.14} isAnimationActive={false} connectNulls />}
            {groups.flatMap((g, i) => {
              const c = MULTI_COLORS[i % MULTI_COLORS.length];
              if (!visible(i)) return [];
              return [
                <Line key={`h${i}`} dataKey={`h${i}`} name={g.name} stroke={c} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />,
                <Line key={`f${i}`} dataKey={`f${i}`} name={`${g.name} (forecast)`} legendType="none" stroke={c} strokeWidth={2} strokeDasharray="5 4" dot={{ r: 2, fill: c }} connectNulls isAnimationActive={false} />,
              ];
            })}
            {boundary_period && <ReferenceLine x={boundary_period} stroke="#818cf8" strokeDasharray="2 2" opacity={0.5} label={{ value: 'now', fontSize: 8, fill: '#818cf8', position: 'top' }} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-2 pt-2 border-t border-border/30 text-[10px] text-muted-foreground leading-relaxed" data-testid="forecast-footnote">
        {focused ? (
          <>
            <span className="text-cyan-300 font-medium">{focused.name} — {focused.model}.</span> {algoDescription(focused.model)} {focused.reason}
            {focused.swing_note && <span className="block mt-1 text-amber-300/90">{focused.swing_note}</span>}
          </>
        ) : (
          <>
            <span className="text-indigo-300 font-medium">Each {breakdown_label} forecast independently</span> (solid = actual, dashed = projection); the best-fitting model was auto-selected per series. Click a series in the legend to isolate it with its confidence band.
            {dropped_periods.length > 0 && <span> Excluded partial period(s): {dropped_periods.join(', ')}.</span>}
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Forecast result chart. Renders history + projection (dashed) + confidence band,
 * an algorithm picker (Auto or a specific one) with a horizon control, and an
 * "algorithm chosen & why" popover. Re-runs against /ai/forecast/series.
 */
export function ForecastChart({ forecast, formatValue }) {
  const [fc, setFc] = useState(forecast);
  const [method, setMethod] = useState(forecast?.mode === 'manual' ? forecast.model : 'auto');
  const [horizon, setHorizon] = useState(forecast?.horizon || 6);
  const [loading, setLoading] = useState(false);
  const [compareMethod, setCompareMethod] = useState('none');
  const [compareSeries, setCompareSeries] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  if (forecast?.multi) return <MultiForecastChart forecast={forecast} formatValue={formatValue} />;
  if (!fc?.series?.length) return null;
  const { series, model, model_description, reason, scorecard = [], measure_label, mode, statsmodels_available, swing_note } = fc;
  const algorithms = forecast?.algorithms || [];

  const rerun = async (nextMethod, nextHorizon) => {
    const histPts = (forecast.series || []).filter(s => s.type === 'history');
    const values = histPts.map(s => s.value);
    const periods = histPts.map(s => s.period);
    if (values.length < 4) { toast.error('Need at least 4 history points'); return; }
    setLoading(true);
    try {
      const res = await api.forecastSeries({ values, periods, horizon: nextHorizon, method: nextMethod });
      const labels = futureLabels(periods, res.forecast.length);
      const newSeries = [
        ...histPts,
        ...res.forecast.map((f, i) => ({ period: labels[i], type: 'forecast', value: f.value, lower: f.lower, upper: f.upper })),
      ];
      setFc({ ...forecast, ...res, series: newSeries });
    } catch (e) { toast.error(e.message || 'Forecast failed'); }
    setLoading(false);
  };

  const onMethod = (v) => { setMethod(v); setCompareMethod('none'); setCompareSeries(null); rerun(v, horizon); };
  const onHorizon = (v) => { const h = Math.max(1, Math.min(parseInt(v, 10) || 6, 36)); setHorizon(h); setCompareMethod('none'); setCompareSeries(null); rerun(method, h); };

  // Compare overlay — forecast the same history with a second algorithm and
  // draw it alongside the primary projection so readers can see how the
  // algorithm choice changes the outlook before trusting it.
  const runCompare = async (m) => {
    setCompareMethod(m);
    if (m === 'none') { setCompareSeries(null); return; }
    const histPts = (fc.series || []).filter(s => s.type === 'history');
    const values = histPts.map(s => s.value);
    const periods = histPts.map(s => s.period);
    if (values.length < 4) { toast.error('Need at least 4 history points'); setCompareMethod('none'); return; }
    setCompareLoading(true);
    try {
      const res = await api.forecastSeries({ values, periods, horizon, method: m });
      const labels = futureLabels(periods, res.forecast.length);
      const pts = {};
      res.forecast.forEach((f, i) => { pts[labels[i]] = f.value; });
      setCompareSeries({ model: res.model || m, points: pts });
    } catch (e) { toast.error(e.message || 'Compare forecast failed'); setCompareMethod('none'); }
    setCompareLoading(false);
  };

  // Grain change re-queries the source at a new bucket size (yearly/quarterly/
  // monthly) — can't be done from the already-bucketed series, so it rebuilds.
  const rebuildGrain = async (u) => {
    if (!fc.model_id) { toast.error('This forecast can’t change grain here.'); return; }
    setLoading(true);
    try {
      const res = await api.forecastRebuild({ model_id: fc.model_id, source_question: fc.source_question, context_question: fc.context_question, unit: u, horizon });
      const nf = res?.result?.forecast;
      if (nf) { setFc(nf); setMethod(nf.mode === 'manual' ? nf.model : 'auto'); setHorizon(nf.horizon || horizon); setCompareMethod('none'); setCompareSeries(null); }
    } catch (e) { toast.error(e.message || 'Could not change grain'); }
    setLoading(false);
  };

  const splitIdx = series.findIndex(s => s.type === 'forecast');
  const data = series.map((s, i) => ({
    period: s.period,
    history: s.type === 'history' ? s.value : (i === splitIdx ? series[splitIdx - 1]?.value : null),
    forecast: s.type === 'forecast' ? s.value : (s.type === 'history' && i === splitIdx - 1 ? s.value : null),
    band: s.type === 'forecast' ? [s.lower, s.upper] : null,
    compare: compareSeries ? (s.type === 'forecast' ? (compareSeries.points[s.period] ?? null) : (i === splitIdx - 1 ? s.value : null)) : null,
  }));

  const fmt = (v) => {
    if (v == null) return '';
    if (formatValue) { const out = formatValue(v); if (out || out === 0) return out; }
    return typeof v === 'number' ? v.toLocaleString() : v;
  };
  const boundaryPeriod = splitIdx > 0 ? series[splitIdx - 1]?.period : null;
  const forecastPoints = series.filter(s => s.type === 'forecast');
  const chosenMape = (scorecard.find(s => s.model === model) || {}).mape;
  const conf = deriveConfidence(chosenMape, forecastPoints);

  return (
    <div className="glass-card rounded-2xl p-4 border border-border/30 relative" data-testid="forecast-chart">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-indigo-400" />
          <span className="text-xs font-heading font-semibold text-foreground">{measure_label} · Forecast</span>
          <ConfidenceBadge conf={conf} />
        </div>
        <div className="flex items-center gap-2">
          {/* Grain picker */}
          {fc.model_id && (fc.grains || []).length > 0 && (
            <select
              value={fc.unit || 'year'}
              onChange={(e) => rebuildGrain(e.target.value)}
              disabled={loading}
              className="text-[10px] bg-background/60 border border-border/40 rounded-lg px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
              data-testid="forecast-grain-select"
              title="Time grain"
            >
              {(fc.grains || ['year', 'quarter', 'month']).map(g => <option key={g} value={g}>{g === 'year' ? 'Yearly' : g === 'quarter' ? 'Quarterly' : g === 'month' ? 'Monthly' : g}</option>)}
            </select>
          )}
          {/* Algorithm picker */}
          <select
            value={method}
            onChange={(e) => onMethod(e.target.value)}
            disabled={loading}
            className="text-[10px] bg-background/60 border border-border/40 rounded-lg px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
            data-testid="forecast-algo-select"
          >
            <option value="auto">Auto (recommended)</option>
            {algorithms.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {/* Compare overlay: second algorithm */}
          {algorithms.length > 0 && (
            <select
              value={compareMethod}
              onChange={(e) => runCompare(e.target.value)}
              disabled={loading || compareLoading}
              className="text-[10px] bg-background/60 border border-border/40 rounded-lg px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-amber-500/40"
              data-testid="forecast-compare-select"
              title="Overlay a second algorithm to compare projections"
            >
              <option value="none">Compare…</option>
              {algorithms.filter(a => a !== model).map(a => <option key={a} value={a}>vs {a}</option>)}
            </select>
          )}
          {compareLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" />}
          {/* Horizon */}
          <div className="flex items-center gap-1">
            <input
              type="number" min={1} max={36} defaultValue={horizon}
              onKeyDown={(e) => { if (e.key === 'Enter') onHorizon(e.target.value); }}
              onBlur={(e) => { if (Number(e.target.value) !== horizon) onHorizon(e.target.value); }}
              disabled={loading}
              className="w-12 text-[10px] bg-background/60 border border-border/40 rounded-lg px-1.5 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
              data-testid="forecast-horizon-input"
            />
            <span className="text-[9px] text-muted-foreground">periods</span>
          </div>
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-400" />}
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 text-[10px] font-medium transition-colors" data-testid="forecast-algo-info" title="Why this algorithm?">
                <Sparkles className="w-3 h-3" />{model}
                <Info className="w-3 h-3 opacity-60" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 overflow-hidden" align="end" data-testid="forecast-algo-popover">
              <div className="px-4 py-3 border-b border-border/40 bg-indigo-500/5">
                <p className="text-xs font-heading font-semibold text-foreground flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-indigo-400" />{mode === 'manual' ? 'You selected' : 'AI chose'}: {model}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{model_description}</p>
              </div>
              <div className="px-4 py-3 space-y-3">
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Why</p>
                  <p className="text-[11px] text-foreground/85 leading-relaxed">{reason}</p>
                </div>
                {scorecard.length > 0 && (
                  <div>
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Model backtest (lower = better)</p>
                    <div className="space-y-1">
                      {scorecard.slice(0, 10).map((s, i) => (
                        <div key={s.model} className="flex items-center justify-between gap-2">
                          <span className={`text-[10px] ${s.model === model ? 'text-indigo-300 font-semibold' : 'text-muted-foreground'}`}>
                            {s.model === model && '★ '}{s.model}
                          </span>
                          <span className={`text-[10px] font-mono ${s.model === model ? 'text-indigo-300' : 'text-muted-foreground/70'}`}>{s.mape}% err</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-[8px] text-muted-foreground/70 italic">
                  Holdout backtest across {scorecard.length} candidate model(s){statsmodels_available ? ' incl. Holt-Winters, ARIMA & SARIMA' : ''}.
                </p>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="w-full" style={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.3} />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#a1a1aa' }} interval={0} />
            <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} tickFormatter={compactAxisValue} width={52} />
            <Tooltip
              contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 11 }}
              formatter={(v, name) => {
                if (name === 'band') return [Array.isArray(v) ? `${fmt(v[0])} – ${fmt(v[1])}` : fmt(v), 'Confidence'];
                if (name === 'compare') return [fmt(v), compareSeries?.model || 'Compare'];
                return [fmt(v), name === 'history' ? 'Actual' : 'Forecast'];
              }}
            />
            <Area dataKey="band" stroke="none" fill="#6366f1" fillOpacity={0.12} isAnimationActive={false} connectNulls />
            <Line dataKey="history" stroke="#22d3ee" strokeWidth={2} dot={false} name="history" isAnimationActive={false} connectNulls />
            <Line dataKey="forecast" stroke="#818cf8" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 2, fill: '#818cf8' }} name="forecast" isAnimationActive={false} connectNulls />
            {compareSeries && <Line dataKey="compare" stroke="#f59e0b" strokeWidth={2} strokeDasharray="2 3" dot={{ r: 2, fill: '#f59e0b' }} name="compare" isAnimationActive={false} connectNulls />}
            {boundaryPeriod && <ReferenceLine x={boundaryPeriod} stroke="#818cf8" strokeDasharray="2 2" opacity={0.5} label={{ value: 'now', fontSize: 8, fill: '#818cf8', position: 'top' }} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-4 mt-2 px-1">
        <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><span className="w-3 h-0.5 bg-cyan-400 inline-block" />Actual</span>
        <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><span className="w-3 h-0.5 inline-block" style={{ background: 'repeating-linear-gradient(90deg,#818cf8,#818cf8 3px,transparent 3px,transparent 5px)' }} />Forecast</span>
        <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><span className="w-3 h-2 bg-indigo-500/20 inline-block rounded-sm" />95% confidence</span>
        {compareSeries && <span className="flex items-center gap-1 text-[9px] text-amber-400" data-testid="forecast-compare-legend"><span className="w-3 h-0.5 inline-block" style={{ background: 'repeating-linear-gradient(90deg,#f59e0b,#f59e0b 2px,transparent 2px,transparent 4px)' }} />{compareSeries.model}</span>}
      </div>
      <p className="mt-2 pt-2 border-t border-border/30 text-[10px] text-muted-foreground leading-relaxed" data-testid="forecast-footnote">
        <span className="text-indigo-300 font-medium">{mode === 'manual' ? 'Algorithm (selected)' : 'Algorithm (auto-chosen)'}: {model}.</span> {algoDescription(model)} {reason}
      </p>
      {swing_note && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 px-2.5 py-1.5 text-[10px] text-amber-300/90 leading-relaxed" data-testid="forecast-swing-note">
          <TrendingUp className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{swing_note}</span>
        </p>
      )}
    </div>
  );
}
